import _ from 'lodash'
import Backbone from 'backbone'
import moment from 'moment'
import globals from 'scoreboard/components/globals'
import i18n from 'scoreboard/components/i18n'

// we expose calendars.calendarsById and calendars.periodsById

/**
 * A few different properties will be exposed in this object, but they can't
 * be populated until after Backbone loads.  The properties are:
 *
 * calendars - the array of calendars, in the user-specified order
 * defaultPeriods - the IDs of the default periods, keyed by calendar ID
 * calendarsById - the full calendar objects keyed by ID
 * periodsById - each calendar period keyed by ID
 *
 * Each calendar has a periods property, with all periods in chronological order
 *
 * Finally, each period will have a calendar property attached to it.
 */
function Calendars() { }
_.extend(Calendars.prototype, Backbone.Events)

var calendars = new Calendars()

// provided so moment doesn't have to guess the format, which speeds things up
var datePattern = 'YYYY-MM-DD'

function setGlobalsPeriodId() {
	if (_.isEmpty(calendars.calendars)) {
		globals.periodId = null
	} else {
		globals.periodId = calendars.defaultPeriodId()
	}
}

/**
 * Returns the current "default period" for this database. This is the taken
 * from the application's default calendar and uses the app-level
 * "Default Calendar Period" setting.
 *
 * @return {Integer}
 */
calendars.defaultPeriodId = function () {
	// the default calendar is the first calendar
	var defaultCalendar = calendars.calendars[0].id
	return parseInt(calendars.defaultPeriods[defaultCalendar])
}

Backbone.on('load', function(data) {
	if (data.calendars) {
		calendars.reset(data)
	}

	if (data.calendars && data.calendars.length && !globals.periodId) {
		setGlobalsPeriodId()
	}
})

calendars.reset = function(data) {
	this.calendars = data.calendars
	this.defaultPeriods = data.defaultPeriods

	// construct calendars.calendarsById
	this.calendarsById = _.keyBy(data.calendars, 'id')

	// construct calendars.periodsById
	var periods = _.flatten(_.map(data.calendars, 'periods'))
	periods = _.reject(periods, _.isUndefined)
	this.periodsById = _.keyBy(periods, 'id')

	_.each(periods, function(period) {
		period.start = moment(period.start, datePattern)
		period.end = moment(period.end, datePattern)
	})

	// attach calendars to each period
	_.each(data.calendars, function(calendar) {
		_.each(calendar.periods, function(period) {
			period.calendar = calendar
		})
	})

	// if the old global calendar period doesn't exist
	if (!this.periodsById[globals.periodId]) {
		setGlobalsPeriodId()
	}

	// We are assuming that calendar.periods comes from the server in
	// chronological order. If that is incorrect or changes, we'll need
	// to sort it manually here.

	this.trigger('reset')
}

// saves a new version of a calendar so that its data is fully accessible
// by the calendars module.  can save a new calendar or update an existing one
calendars.save = function(calendar) {
	// if this is an existing calendar
	if (this.calendarsById[calendar.id]) {
		_.each(this.calendars, function(c, index) {
			if (c.id == calendar.id) {
				this.calendars[index] = c
			}
		}.bind(this))
	} else {
		this.calendars.push(calendar)
	}
	if (calendar.defaultPeriodId) {
		this.defaultPeriods[calendar.id] = calendar.defaultPeriodId
	}
	this.reset({ calendars: this.calendars, defaultPeriods: this.defaultPeriods })
}

// Returns a period relative to the given period id. A positive index
// goes into the future (eg. 1 is the next period), a negative to the past
// (eg -1 is the previous period).
calendars.relativePeriod = function (periodId, relativeIndex) {
	var relativePeriods = this.relativePeriods(periodId, relativeIndex, relativeIndex)
	return relativePeriods.length === 0 ? null : relativePeriods[0]
}

//Returns a period relative to the given period id. A positive index
//goes into the future (eg. 1 is the next period), a negative to the past
//(eg -1 is the previous period).  If the relativeIndex extends beyond the
//defined list of periods, returns the closest period to the desired one
calendars.safeRelativePeriod = function (periodId, relativeIndex) {
	var period = this.periodsById[periodId]
	if (!period) {
		return null
	}

	var cal = period.calendar

	var index = _.findIndex(cal.periods, function (p) { return p.id == periodId })
	var adjusted = relativeIndex + index
	if (adjusted < 0) {
		return _.first(cal.periods)
	}
	if (adjusted >= cal.periods.length) {
		return _.last(cal.periods)
	}
	return cal.periods[adjusted]
}

//Returns a range of periods relative to the given period id. A positive index
//goes into the future (eg. 1 is the next period), a negative to the past
//(eg -1 is the previous period).
calendars.relativePeriods = function(periodId, start, end) {
	var period = this.periodsById[periodId]
	if (!period) {
		return []
	}

	var cal = period.calendar

	var referencePeriodIndex = _.findIndex(cal.periods, function (p) { return p.id == periodId })
	var rawStartIndex = start + referencePeriodIndex
	var rawEndIndex = 1 + end + referencePeriodIndex

	// if none of our indexes are in bounds, then don't bother with
	// min/max logic; just return an empty array
	if (rawStartIndex < 0 && rawEndIndex <= 0 ) {
		return []
	}

	// can't start any lower than 0
	var startIndex = Math.max(rawStartIndex, 0)
	// can't end any higher than the end of the array or less than 1
	// (less than 1 would give us nothing, since the end index is exclusive)
	var endIndex = Math.max(Math.min(rawEndIndex, cal.periods.length), 1)

	return cal.periods.slice(startIndex, endIndex)
}

// Given a target calendar ID and a period ID from any calendar, will return the earliest
// period in the target calendar that ends on or after the provided period's end date.
// If no such period exists, it will return the latest period in the target calendar.
//
// Purpose: this basically figures out which period in the target calendar most closely
// corresponds to a given calendar period from another calendar. Example: when a user is
// set to a different calendar than what is displayed in a chart.
//
// Adapted from ChartServiceImpl.getPeriodRelativeToTarget()
calendars.correspondingPeriod = function (targetCalendarId, knownPeriodId) {
	var targetCalendar = this.calendarsById[targetCalendarId]
	var knownPeriod = this.periodsById[knownPeriodId]

	if (knownPeriod) {
		if (knownPeriod.calendar.id == targetCalendarId) {
			return knownPeriod
		}
		var result = _.find(targetCalendar.periods, function (p) {
			return p.end.isSame(knownPeriod.end) || p.end.isAfter(knownPeriod.end)
		})

		if (result) {
			return result
		}
	}

	return targetCalendar.periods[targetCalendar.periods.length - 1]
}

calendars.periodSublist = function (startPeriodId, endPeriodId) {
	var start = this.periodsById[startPeriodId]
	var end = this.periodsById[endPeriodId]
	if (!start || !end) {
		return null
	}
	return _.filter(this.calendarsById[start.calendar.id].periods, function (p) {
		if (p.id == start.id || p.id == end.id) {
			return true
		}
		return p.start.isAfter(start.start) && p.start.isBefore(end.start)
	})
}

// Returns the relative distance between two periods. A negative result indicates
// that the second period is before the first one
//
// Examples:
//   distance(jan2014, march2014) is 2
//   distance(march2014, jan2014) is -2
calendars.distance = function (beginId, endId) {
	var begin = this.periodsById[beginId]
	var end = this.periodsById[endId]

	if (begin.start.isAfter(end.start)) {
		var negative = true
		var tmpend = end
		end = begin
		begin = tmpend
	}

	var distance = begin.calendar.periods.filter(function (p) {
		return (p.start.isSame(begin.start) || p.start.isAfter(begin.start))
			&& (p.end.isSame(end.end) || p.end.isBefore(end.end))
	}).length - 1

	return negative ? (-1 * distance) : distance
}

/**
 * Given a range object and a period for reference, returns all calendar periods in that range.
 *
 * @param {Object} range options that describe the range
 * @param {number} range.calendarId the range calendar;
 * 					null indicates the reference period's calendar should be used
 * @param {number} range.startPeriodId the period at which the range should start;
 * 					null implies relative ranges are used
 * @param {number} range.endPeriodId the period at which the range should start;
 * 					null implies relative ranges are used
 * @param {number} range.startPeriodDelta the relative period delta at which
 * 					the range should start; null implies absolute ranges are used
 * @param {number} range.endPeriodDelta the relative period delta at which
 * 					the range should end; null implies absolute ranges are used
 * @param {number} currentPeriodId the ID of the period to be used as a reference point
 * 					for relative periods
 *
 * Note that only absolute period IDs OR deltas should be used; if both are provided,
 * absolute period IDs will be used
 */
calendars.resolveRange = function (range, currentPeriodId) {
	let calendarId = toInt(range.calendarId)
	var rangePeriodId = calendarId
		? calendars.correspondingPeriod(calendarId, currentPeriodId).id
		: currentPeriodId

	let startPeriodId = toInt(range.startPeriodId)
	let endPeriodId = toInt(range.endPeriodId)
	if (startPeriodId && endPeriodId) {
		return calendars.periodSublist(startPeriodId, endPeriodId)
	} else {
		return calendars.relativePeriods(rangePeriodId,
			toInt(range.startPeriodDelta), toInt(range.endPeriodDelta))
	}
}

/**
 * Returns a descriptive "count" message appropriate for the given standard calendar.
 *
 * Examples: '1 week', '2 weeks', '3 years'
 */
calendars.periodCount = function (calendarId, count) {
	let calendar = calendars.calendarsById[calendarId]
	let prefix
	switch (calendar.type) {
		case 'weekly':
			prefix = 'common.time.week'; break;
		case 'monthly':
			prefix = 'common.time.month'; break;
		case 'quarterly':
		case 'fiscal-quarterly':
			prefix = 'common.time.quarter'; break;
		case 'yearly':
		case 'fiscal-yearly':
			prefix = 'common.time.year'; break;
		default:
			throw new Error('only standard calendars supported for periodCount')
	}

	return i18n.countMessage(prefix, count)
}

/**
 * Returns whether the period is in an aggregated calendar
 */
calendars.isAggregatedPeriod = function(calendarId, periodId) {
	if (!periodId) throw Error('periodId must be specified')
	let aggregatedPeriod
	if (_.isNil(calendarId)) {
		aggregatedPeriod = false
	} else {
		let currCalSize = this.periodsById[periodId].calendar.avgPeriodSize
		let nativeCalSize = this.calendarsById[calendarId].avgPeriodSize
		aggregatedPeriod = currCalSize > nativeCalSize
	}
	return aggregatedPeriod
}

function toInt(val) {
	if (_.isNumber(val)) {
		return val
	}

	return _.isNil(val) ? null : parseInt(val)
}

_.extend(calendars, {
	// calendar types from CalendarType.java
	CUSTOM: 'custom',
	DAILY: 'daily',
	WEEKLY: 'weekly',
	MONTHLY: 'monthly',
	QUARTERLY: 'quarterly',
	YEARLY: 'yearly',
	FISCAL_QUARTERLY: 'fiscal-quarterly',
	FISCAL_YEARLY: 'fiscal-yearly'
})
export default calendars

calendars.__filename__ = 'components/calendars.js'
